export const ALL_LOCALES = [
    "de",
    "en",
    "es",
    "fr",
    "hi",
    "it",
    "nl",
    "pl",
    "pt",
    "ro",
    "tr",
    "zh"
] as const;

export type Locale = (typeof ALL_LOCALES)[number];

export const DEFAULT_LOCALE: Locale = "en";

/**
 * Get the native display name for a locale using the Intl API. Accepts any
 * BCP-47 code (not just a known `Locale`) since it is fully defensive — an
 * unknown or malformed code falls back to the code itself.
 */
export function getLocaleName(locale: Locale | string): string {
  try {
    const name =
      new Intl.DisplayNames([locale], { type: "language" }).of(locale) ??
      locale;
    return name[0]?.toLocaleUpperCase(locale) + name.slice(1);
  } catch {
    return locale;
  }
}

/**
 * Whether a locale matches a user search query, by display name or by code
 * (e.g. "fre" and "fr" both match French). The canonical matcher for any
 * locale picker UI.
 */
export function localeMatchesSearch(
  locale: Locale | string,
  query: string,
): boolean {
  const q = query.trim().toLocaleLowerCase();
  if (!q) {
    return true;
  }
  return (
    getLocaleName(locale).toLocaleLowerCase().includes(q) || locale.includes(q)
  );
}

/** Filter locale codes down to those matching a user search query. */
export function searchLocales<T extends Locale | string>(
  locales: readonly T[],
  query: string,
): readonly T[] {
  if (!query.trim()) {
    return locales;
  }
  return locales.filter((locale) => localeMatchesSearch(locale, query));
}
